import { useParams, useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import "react-phone-number-input/style.css";
import Layout from "../../components/layout/Layout";
import { Header } from "../../components/typography/Header";
import {
  FONT_SIZE_MEDIUM,
  FONT_SIZE_XSMALL,
  COLOR_APP_TEXT,
  TEXT_FONT_FAMILY,
  COLOR_TERTIARY,
  MOBILE_SCREEN,
} from "../../components/theme";
import {
  useGetQuestionnaireSectionDetailsQuery,
  useGetQuestionnaireSectionsQuery,
  useGetQuestionnaireSectionGroupsByDocumentIdQuery,
} from "../../api/questionnaire";
import Loading from "../../components/Loading";
import Error from "../../components/Error";
import { skipToken, QueryStatus } from "@reduxjs/toolkit/query/react";
import VideoContainer from "../../components/VideoContainer";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";
import { navigateToNextSection, scrollToTop } from "./utils/navigation";
import GroupTitle from "./GroupTitle";
import ProcessOverview from "./ProcessOverview";
import UserFullNameForm from "./userInformation/UserFullNameForm";
import UserInformationGroup from "./userInformation/UserInformationGroup";
import QuestionTreeRenderer from "./QuestionTreeRenderer";
import { QuestionnaireSectionStatus } from "../../../../api/src/types";
import QuestionnairePopupManager from "./utils/QuestionnairePopupManager";
import QuestionnaireAccessBlocker from "./QuestionnaireAccessBlocker";
import { useQuestionnaireSectionState } from "../../hooks/useQuestionnaireSectionState";
import useCurrentUserPaymentData from "../../hooks/useCurrentUserPaymentData";
import RepresentationBlocker from "../../components/RepresentationBlocker";
import { useGetAnswersWithAttorneyRestrictionsQuery } from "../../api/answers";
import { getSectionVisibility } from "./utils";
import QuestionnairePaywall from "./QuestionnairePaywall";

export const StyledDescription = styled.div`
  font-family: ${TEXT_FONT_FAMILY};
  font-size: ${FONT_SIZE_MEDIUM};
  color: ${COLOR_APP_TEXT};
  font-weight: 300;

  .ql-editor p span {
    font-size: inherit !important;
  }
  .ql-editor {
    font-family: inherit !important;
    font-size: inherit !important;
    color: inherit;
    font-weight: inherit !important;
    line-height: inherit;
    padding: 0;
    margin: 0;
  }
  .ql-editor strong[style] {
    font-size: inherit !important;
    color: inherit !important;
    font-weight: inherit !important;
  }
  a {
    text-decoration: underline;
    color: ${COLOR_APP_TEXT} !important;
    font-family: ${TEXT_FONT_FAMILY};
    font-size: ${FONT_SIZE_MEDIUM};
  }
  strong {
    font-weight: 600 !important;
  }
  ol {
    counter-reset: step-counter;
    list-style: none;
    padding-left: 0;
    margin-left: 0;
  }
  ol li {
    counter-increment: step-counter;
    position: relative;
    padding-left: 24px;
    margin-bottom: 12px;
  }
  ol li::before {
    content: counter(step-counter);
    position: absolute;
    left: 0;
    top: 0.25rem;
    width: 16px;
    height: 16px;
    background: ${COLOR_APP_TEXT};
    color: ${COLOR_TERTIARY};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${FONT_SIZE_XSMALL};
  }
`;

const StyledHeader = styled(Header)`
  margin-bottom: 24px;
`;

const StyledGroup = styled.div`
  margin-top: 24px;
`;

const StyledContinueButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 24px;
  ${MOBILE_SCREEN} {
    margin-top: 16px;
    width: 100%;
  }
`;

const StyledContinueButton = styled(PrimaryButton)`
  width: 326px;
  ${MOBILE_SCREEN} {
    width: 100%;
  }
`;

const Questionnaire = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Debounced saving state to prevent continue button flashing
  const [isSavingDebounced, setIsSavingDebounced] = useState(false);

  // Fetch user, document, and sections information
  const {
    user,
    document: userDocument,
    currentUserWillHireAttorney,
    isPrenupPaid,
    representationPayment,
    isLoading: isUserDocumentLoading,
    userDocumentError,
    userDocumentLoading,
  } = useCurrentUserPaymentData();

  const {
    data: answersWithAttorneyRestrictions,
    isLoading: isAnswersWithAttorneyRestrictionsLoading,
    error: answersWithAttorneyRestrictionsError,
  } = useGetAnswersWithAttorneyRestrictionsQuery(
    user && userDocument?.stateId && !userDocumentLoading && !userDocumentError
      ? { userId: user.id }
      : skipToken,
  );

  // Fetch section access blocker meta information
  const {
    isQuestionnaireBlocked,
    isDocumentGenerated,
    hasAnswersModifiedAfterDocumentGeneration,
    isDocumentInEditor,
    isDocumentInProof,
    isLoading: isSectionAccessBlockerLoading,
    error: isSectionAccessBlockerError,
  } = useQuestionnaireSectionState();

  const [
    shouldValidateQuestionnaireSection,
    setShouldValidateQuestionnaireSection,
  ] = useState(false);
  const [validationMessagePopupOpen, setValidationMessagePopupOpen] =
    useState(false);
  const [
    documentGeneratedWarningPopupOpen,
    setDocumentGeneratedWarningPopupOpen,
  ] = useState(false);

  // Function to scroll to the first unanswered question
  const scrollToFirstUnansweredQuestion = () => {
    // Use data attribute for fast, reliable targeting
    const errorCard = document.querySelector('[data-validation-error="true"]');

    if (errorCard) {
      errorCard.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    // Fallback: scroll to top if no error cards found
    scrollToTop();
  };

  // Fetch sections information
  const {
    data: sections,
    isLoading: isSectionsLoading,
    isError: isSectionsError,
  } = useGetQuestionnaireSectionsQuery(
    user?.id
      ? { id: user.id, includeStatus: isPrenupPaid ? true : false }
      : skipToken,
  );

  const currentSection = sections?.find(
    (s) => section === s.label.toLowerCase().replace(/ /g, "-"),
  );

  // Check if current section is the last section
  const isLastSection = !sections?.some(
    (s) => s.order > (currentSection?.order ?? 0),
  );

  const {
    data: sectionDetails,
    isLoading: isSectionDetailsLoading,
    isError: isSectionDetailsError,
  } = useGetQuestionnaireSectionDetailsQuery(
    user?.id && currentSection?.id
      ? { id: user.id, section: currentSection.id }
      : skipToken,
  );

  const {
    data: stateRestrictionAppliedGroups,
    isLoading: isStateRestrictionAppliedGroupsLoading,
    isError: isStateRestrictionAppliedGroupsError,
  } = useGetQuestionnaireSectionGroupsByDocumentIdQuery(
    user?.id &&
      currentSection?.id &&
      userDocument?.stateId &&
      (isPrenupPaid || sectionDetails?.order === 1)
      ? { docId: userDocument.id, section: currentSection.id }
      : skipToken,
  );

  const isAnswerSaving = useSelector((state: RootState) => {
    // Check all relevant API slices
    const allMutations = [
      ...Object.values(state.answersApi.mutations),
      ...Object.values(state.usersApi.mutations), // User related questions : Name, Gender, Birth Date, Phone Number
      ...Object.values(state.documentsApi.mutations), // Document related questions : State
    ];
    const isSaving = allMutations.some(
      (mutation) => mutation?.status === QueryStatus.pending,
    );
    return isSaving;
  });

  const isStateAware = Boolean(userDocument?.stateId);
  const filteredGroups = stateRestrictionAppliedGroups?.flatMap((group) => {
    const filteredQuestions = group.questions.filter((q) =>
      isStateAware
        ? q.parentQuestionId === null
        : !q.questionStateRestrictions.length && q.parentQuestionId === null,
    );
    return filteredQuestions.length
      ? [{ ...group, questions: filteredQuestions }]
      : [{ ...group, questions: [] }];
  });

  // Main loading state for the page
  const isLoading =
    isSectionDetailsLoading ||
    isUserDocumentLoading ||
    isSectionsLoading ||
    isSectionAccessBlockerLoading ||
    isAnswersWithAttorneyRestrictionsLoading;

  // Loading state for groups data
  const isGroupsDataLoading = isStateRestrictionAppliedGroupsLoading;

  const isAccessBlockerError =
    userDocument?.stateId &&
    (isSectionAccessBlockerError ?? answersWithAttorneyRestrictionsError);

  // Error state for the page
  const isError =
    isSectionDetailsError || isSectionsError || isAccessBlockerError;

  // Error state for groups data
  const isGroupsDataError = isStateRestrictionAppliedGroupsError;

  // Scroll to top when location changes (i.e., after navigation)
  // Reset validation message popup and validate questionnaire section when location changes
  useEffect(() => {
    setShouldValidateQuestionnaireSection(false);
    setValidationMessagePopupOpen(false);
    scrollToTop();
  }, [location.pathname]);

  // Scroll to first unanswered question when validation is triggered
  useEffect(() => {
    if (shouldValidateQuestionnaireSection) {
      // Longer delay to ensure DOM is fully updated with validation errors
      setTimeout(() => {
        scrollToFirstUnansweredQuestion();
      }, 300);
    }
  }, [shouldValidateQuestionnaireSection]);

  // Debounce the saving state to prevent continue button flashing
  useEffect(() => {
    if (isAnswerSaving) {
      setIsSavingDebounced(true);
      return;
    } else {
      // Delay the reset to prevent rapid flashing
      const timer = setTimeout(() => {
        setIsSavingDebounced(false);
      }, 200); // 200ms delay
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isAnswerSaving]);

  // Update documentGeneratedWarningPopupOpen when dependencies change
  useEffect(() => {
    const shouldShowWarning = Boolean(
      isDocumentGenerated &&
        userDocument?.id &&
        !hasAnswersModifiedAfterDocumentGeneration &&
        !isDocumentInEditor &&
        !isDocumentInProof,
    );

    setDocumentGeneratedWarningPopupOpen(shouldShowWarning);
  }, [
    isDocumentGenerated,
    userDocument?.id,
    hasAnswersModifiedAfterDocumentGeneration,
    isDocumentInEditor,
    isDocumentInProof,
  ]);

  const handleSectionValidationOnConfirm = () => {
    setValidationMessagePopupOpen(false);
    setShouldValidateQuestionnaireSection(true);
  };

  const handleSectionValidationOnCancel = () => {
    setValidationMessagePopupOpen(false);
    setShouldValidateQuestionnaireSection(false);
    if (currentSection && sections) {
      navigateToNextSection(sections, currentSection, navigate);
    }
  };
  // Navigation logic for "Continue" button
  const handleContinue = async () => {
    setShouldValidateQuestionnaireSection(false);

    // Priority -> Document Purchase -> Attorney Rules -> Section Completion
    if (!sections || !currentSection) return;

    // If user hasn't purchased Document, redirect to payment
    if (!isPrenupPaid && userDocument?.stateId) {
      await navigate("/payment");
      scrollToTop();
      return;
    }

    const currentSectionHasAttorneyRules = sections.find(
      (item) => item.id === currentSection.id,
    )?.attorneyRequiredQuestionPresentSection;

    if (
      !representationPayment &&
      currentSectionHasAttorneyRules &&
      currentUserWillHireAttorney
    ) {
      await navigate("/attorney-services?serviceType=REPRESENTATION");
      return;
    }

    // Navigate to next section if the section is completed
    const isSectionCompleted =
      sectionDetails?.sectionStatus === QuestionnaireSectionStatus.COMPLETED;

    // If section is completed and user has document purchase and is not the last section, navigate to next section
    if (isSectionCompleted && isPrenupPaid && !isLastSection) {
      navigateToNextSection(sections, currentSection, navigate);
      return;
    }

    // If section is completed and user has document purchase and is the last section,
    // navigate to next section only if all other sections are completed
    if (isLastSection) {
      const isEveryOtherSectionCompleted = sections.every(
        (s) => s.status === QuestionnaireSectionStatus.COMPLETED,
      );
      if (isEveryOtherSectionCompleted) {
        navigateToNextSection(sections, currentSection, navigate);
        return;
      }
    }

    // Otherwise, show validation popup for incomplete sections
    setValidationMessagePopupOpen(true);
  };

  if (isError) {
    return (
      <Layout>
        <Error>
          Refresh the page and if the problem persists use the chat bubble to
          reach out to our support team.
        </Error>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  // If no document found, display only the user full name form
  // Continue button takes users to payment page
  if (!userDocument) {
    return (
      <Layout>
        <UserFullNameForm />
        <StyledContinueButtonContainer>
          <StyledContinueButton onClick={() => navigate("/payment")}>
            Continue
          </StyledContinueButton>
        </StyledContinueButtonContainer>
      </Layout>
    );
  }

  if (
    isPrenupPaid &&
    currentUserWillHireAttorney &&
    !representationPayment &&
    !getSectionVisibility(
      sections,
      currentSection,
      answersWithAttorneyRestrictions,
      userDocument.stateId,
    )
  ) {
    return (
      <Layout>
        <RepresentationBlocker />
      </Layout>
    );
  }

  /**Block access to questionnaire if prenup is not paid*/
  if (!isPrenupPaid && sectionDetails?.order !== 1) {
    return (
      <QuestionnairePaywall
        sectionName={sectionDetails?.name ?? "Questionnaire"}
        sectionDescription={sectionDetails?.description ?? ""}
      />
    );
  }

  /**Block access to questionnaire if the document is edited, in proof, or notarization is complete */
  if (isQuestionnaireBlocked && isPrenupPaid) {
    return (
      <Layout>
        <QuestionnaireAccessBlocker documentId={userDocument.id} />
      </Layout>
    );
  }

  return (
    <Layout>
      <>
        {/** Render the popup on first visit to the questionnaire page if the document is generated and does not have answers modified after document generation */}
        {documentGeneratedWarningPopupOpen && (
          <QuestionnairePopupManager
            validationMessagePopupOpen={documentGeneratedWarningPopupOpen}
            setValidationMessagePopupOpen={setDocumentGeneratedWarningPopupOpen}
            userDocument={userDocument}
            isLastSection={false}
            isDocumentGenerated={isDocumentGenerated}
            hasAnswersModifiedAfterDocumentGeneration={
              hasAnswersModifiedAfterDocumentGeneration
            }
            isDocumentInEditor={isDocumentInEditor}
            isDocumentInProof={isDocumentInProof}
            onConfirm={() => {
              setDocumentGeneratedWarningPopupOpen(false);
            }}
            onCancel={() => navigate("/documents")}
          />
        )}

        {/** Render the popup if the section is incomplete */}
        <QuestionnairePopupManager
          validationMessagePopupOpen={validationMessagePopupOpen}
          userDocument={userDocument}
          isLastSection={isLastSection}
          setValidationMessagePopupOpen={setValidationMessagePopupOpen}
          onConfirm={handleSectionValidationOnConfirm}
          onCancel={handleSectionValidationOnCancel}
        />

        <StyledHeader>{sectionDetails?.name}</StyledHeader>
        <StyledDescription
          dangerouslySetInnerHTML={{
            __html: sectionDetails?.description ?? "",
          }}
        />

        {/**Render Process Overview if the section is the first section and user has selected a state*/}
        {sectionDetails?.order === 1 && userDocument.stateId && (
          <ProcessOverview />
        )}

        {sectionDetails?.sectionKey === "background_information" && (
          <UserFullNameForm
            shouldValidateQuestionnaireSection={
              shouldValidateQuestionnaireSection
            }
          />
        )}

        {isGroupsDataError && (
          <Error>
            Could not load questions. Refresh the page and if the problem
            persists use the chat bubble to reach out to our support team.
          </Error>
        )}

        {isGroupsDataLoading ? (
          <Loading />
        ) : (
          filteredGroups &&
          filteredGroups.length > 0 &&
          filteredGroups.map((group) => (
            <StyledGroup key={group.id}>
              {group.title && (
                <GroupTitle title={group.title} groupIcon={group.groupIcon} />
              )}{" "}
              {group.description && (
                <StyledDescription
                  dangerouslySetInnerHTML={{
                    __html: group.description,
                  }}
                />
              )}
              {group.videos.length > 0 && (
                <VideoContainer
                  videoDetails={{
                    youtubeLink: group.videos[0]?.youtubeLink ?? "",
                    title: group.videos[0]?.title ?? "",
                    author: group.videos[0]?.author ?? "",
                  }}
                />
              )}
              {/** Render User Gender, Birth Date, and Phone Number Questions for groups with "Identity" title */}
              {/* This avoids duplicate user gender, birth date, and phone number in answer record and user table and gives the single source of truth for user gender, birth date, and phone number */}
              {/* Avoids the need to handle questionIds for user gender, birth date, and phone number in different environments */}
              {sectionDetails?.sectionKey === "understanding" &&
                group.groupKey === "identity" && (
                  <UserInformationGroup
                    shouldValidateQuestionnaireSection={
                      shouldValidateQuestionnaireSection
                    }
                  />
                )}
              {group.questions.map((question) => {
                return (
                  <div key={question.id}>
                    <QuestionTreeRenderer
                      shouldValidateQuestionnaireSection={
                        shouldValidateQuestionnaireSection
                      }
                      question={question}
                      sectionGroupsData={
                        stateRestrictionAppliedGroups
                          ? stateRestrictionAppliedGroups
                          : []
                      }
                    />
                  </div>
                );
              })}
            </StyledGroup>
          ))
        )}
        <StyledContinueButtonContainer>
          <StyledContinueButton
            onClick={handleContinue}
            disabled={isSavingDebounced}
          >
            {isSavingDebounced ? "Saving..." : "Continue"}
          </StyledContinueButton>
        </StyledContinueButtonContainer>
      </>
    </Layout>
  );
};

export default Questionnaire;
